import React from "react";
import { Poptip, Table, Typography } from "@optimizely/axiom";

import styles from "./BulkUploadDetails.module.scss";
import { BulkUpload, BulkUploadFailedEntry } from "../../../domain/BulkUpload";
import { BULK_UPLOAD_RESULT_STATUS } from "../../../constants/constants";
import { EMPTY_VALUE } from "../../../lib/bulk-upload-format";

/**
 * The underlying error message for one failed row. It is a raw backend string of unbounded length, so
 * it is clamped to a single line and the whole of it moved into a tooltip — otherwise one long row
 * dictates the height of the table.
 */
const FailureDetail = ({ detail }: { detail?: string }) => {
    // Nothing to reveal, so no tooltip to attach.
    if (!detail) {
        return <>{EMPTY_VALUE}</>;
    }

    return (
        <Poptip isInline trigger="mouseenter focus" content={detail}>
            <span className={styles["upload-details__detail"]} tabIndex={0}>
                {detail}
            </span>
        </Poptip>
    );
};

/**
 * Why there are no failure rows to show.
 *
 * An empty list is not the same as a clean import, and reporting success for all of these would be
 * wrong: an unreadable file fails without ever reaching a row, a bounded list can be emptied while
 * `failedCount` still stands, and an unfinished job has simply not recorded anything yet.
 */
const emptyStateMessage = ({ resultStatus, failedCount }: Pick<BulkUpload, "resultStatus" | "failedCount">): string => {
    if (!resultStatus) {
        return "Processing has not finished, so no failures have been recorded yet.";
    }

    // `failedCount` stays authoritative even when no entries survived the bound.
    if (failedCount) {
        return `${failedCount} record${
            failedCount === 1 ? "" : "s"
        } failed, but no per-row details were retained for this upload.`;
    }

    // Failed with no failed rows means it never got as far as rows: the file could not be read or parsed.
    if (resultStatus === BULK_UPLOAD_RESULT_STATUS.FAILED) {
        return "This upload failed before any records were processed.";
    }

    return "All records uploaded successfully.";
};

/**
 * The rows that failed to process, for the upload detail sidebar's Failures tab.
 *
 * `failedEmails` is bounded independently of `failedCount`, so when the retained count falls short of
 * the total this says so outright — a silently short list reads as "only these three failed", which
 * would understate the damage.
 */
export const BulkUploadFailuresTab = ({
    failedEmails,
    failedEmailsRetained,
    failedCount,
    resultStatus
}: Pick<BulkUpload, "failedEmails" | "failedEmailsRetained" | "failedCount" | "resultStatus">) => {
    if (!failedEmails?.length) {
        return (
            <div className={styles["upload-details__section"]} data-testid="upload-details-failures">
                <Typography type="body" data-testid="upload-details-failures-empty">
                    {emptyStateMessage({ resultStatus, failedCount })}
                </Typography>
            </div>
        );
    }

    const retained = failedEmailsRetained ?? failedEmails.length;
    const isTruncated = typeof failedCount === "number" && retained < failedCount;

    return (
        <div className={styles["upload-details__section"]} data-testid="upload-details-failures">
            {isTruncated && (
                <Typography type="caption" className="push--bottom" data-testid="upload-details-failures-truncated">
                    Showing {retained} of {failedCount} failures. The rest were not retained.
                </Typography>
            )}

            <Table density="loose" data-testid="upload-details-failures-table">
                <Table.THead>
                    <Table.TR>
                        <Table.TH sorting={{}} width="35%">
                            Email
                        </Table.TH>
                        <Table.TH sorting={{}} width="30%">
                            Reason
                        </Table.TH>
                        <Table.TH sorting={{}} width="35%">
                            Details
                        </Table.TH>
                    </Table.TR>
                </Table.THead>
                <Table.TBody>
                    {failedEmails.map((entry: BulkUploadFailedEntry, index: number) => (
                        <Table.TR key={`${entry.email ?? "unknown"}-${index}`}>
                            <Table.TD>{entry.email || EMPTY_VALUE}</Table.TD>
                            {/* Rows recorded before per-row reasons existed genuinely have none, so
                                say that rather than invent a cause. */}
                            <Table.TD>{entry.reason || "No reason recorded"}</Table.TD>
                            <Table.TD>
                                <FailureDetail detail={entry.detail} />
                            </Table.TD>
                        </Table.TR>
                    ))}
                </Table.TBody>
            </Table>
        </div>
    );
};

BulkUploadFailuresTab.displayName = "BulkUploadFailuresTab";
